import React, { useCallback, useState } from 'react';
import { Box, Chip, Dialog, Grid, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { ChevronLeft, ChevronRight, Close, Collections } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useEmblaCarousel from 'embla-carousel-react';
import { OrderPageImageData } from '../../../api';
import { optimizeImageUrl } from '../../../utils/orderPageImageUtils';

const MOBILE_HEIGHT = 350;
const DESKTOP_HEIGHT = 500;
const IMAGE_BORDER_RADIUS = 8;
const MAX_VISIBLE_IMAGES = 3;
const GALLERY_MAX_WIDTH = 1300;
const DESKTOP_MAIN_IMAGE_WIDTH = 900;
const DESKTOP_SECONDARY_IMAGE_WIDTH = 450;
const MOBILE_IMAGE_WIDTH = 600;
const GALLERY_MODAL_IMAGE_WIDTH = 1200;

interface HeroGalleryProps {
  images: OrderPageImageData[];
  experienceName: string;
  fallbackImage?: string;
}

export const HeroGallery = ({ images, experienceName, fallbackImage }: HeroGalleryProps) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isRtl = i18n.dir() === 'rtl';
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const sortedImages = [...images].sort((a, b) => a.order - b.order);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'start',
    direction: isRtl ? 'rtl' : 'ltr',
    slidesToScroll: 1,
  });

  const [galleryEmblaRef, galleryEmblaApi] = useEmblaCarousel({
    loop: true,
    align: 'center',
    direction: isRtl ? 'rtl' : 'ltr',
    startIndex: galleryIndex,
  });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const galleryScrollPrev = useCallback(() => {
    if (galleryEmblaApi) galleryEmblaApi.scrollPrev();
  }, [galleryEmblaApi]);

  const galleryScrollNext = useCallback(() => {
    if (galleryEmblaApi) galleryEmblaApi.scrollNext();
  }, [galleryEmblaApi]);

  const openGallery = (index = 0) => {
    setGalleryIndex(index);
    setGalleryOpen(true);
  };

  // If no images, show fallback or placeholder
  if (sortedImages.length === 0 && !fallbackImage) {
    return (
      <Box
        sx={{
          height: isSmallScreen ? MOBILE_HEIGHT : DESKTOP_HEIGHT,
          backgroundColor: '#f5f5f5',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Box sx={{ fontSize: 80, opacity: 0.3 }}>🎨</Box>
      </Box>
    );
  }

  const displayImages =
    sortedImages.length > 0 ? sortedImages : fallbackImage ? [{ value: fallbackImage, type: 'image', order: 0 }] : [];

  const renderMedia = (
    image: OrderPageImageData | { value: string; type: string; order: number },
    alt: string,
    maxWidth: number,
    rounded = false,
  ) => {
    const style: React.CSSProperties = {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      borderRadius: rounded ? IMAGE_BORDER_RADIUS : 0,
    };

    if (image.type === 'video') {
      return (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video src={image.value} controls style={style} />
      );
    }
    return <img src={optimizeImageUrl(image.value, maxWidth)} alt={alt} style={style} />;
  };

  const renderGalleryModal = () => (
    <Dialog
      open={galleryOpen}
      onClose={() => setGalleryOpen(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.95)',
          maxHeight: '90vh',
        },
      }}>
      <Box sx={{ position: 'relative', height: '80vh' }}>
        <IconButton
          onClick={() => setGalleryOpen(false)}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 2,
            color: 'white',
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
          }}>
          <Close />
        </IconButton>

        <Box ref={galleryEmblaRef} sx={{ overflow: 'hidden', height: '100%' }}>
          <Grid container flexWrap="nowrap" sx={{ height: '100%' }}>
            {displayImages.map((image, index) => (
              <Box
                key={index}
                sx={{
                  flex: '0 0 100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 4,
                }}>
                {image.type === 'video' ? (
                  // eslint-disable-next-line jsx-a11y/media-has-caption
                  <video
                    src={image.value}
                    controls
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <img
                    src={optimizeImageUrl(image.value, GALLERY_MODAL_IMAGE_WIDTH)}
                    alt={`${experienceName} ${index + 1}`}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      borderRadius: IMAGE_BORDER_RADIUS,
                    }}
                  />
                )}
              </Box>
            ))}
          </Grid>
        </Box>

        {displayImages.length > 1 && (
          <>
            <IconButton
              onClick={galleryScrollPrev}
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1,
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
              }}>
              {isRtl ? <ChevronRight /> : <ChevronLeft />}
            </IconButton>
            <IconButton
              onClick={galleryScrollNext}
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1,
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
              }}>
              {isRtl ? <ChevronLeft /> : <ChevronRight />}
            </IconButton>
          </>
        )}
      </Box>
    </Dialog>
  );

  if (displayImages.length === 1) {
    const image = displayImages[0];
    return (
      <>
        <Box
          onClick={() => openGallery(0)}
          sx={{
            height: isSmallScreen ? MOBILE_HEIGHT : DESKTOP_HEIGHT,
            borderRadius: 2,
            overflow: 'hidden',
            cursor: 'pointer',
          }}>
          {renderMedia(image, experienceName, isSmallScreen ? MOBILE_IMAGE_WIDTH : GALLERY_MAX_WIDTH, true)}
        </Box>
        {renderGalleryModal()}
      </>
    );
  }

  // Mobile: Horizontal carousel
  if (isSmallScreen) {
    return (
      <>
        <Box sx={{ position: 'relative' }}>
          <Box ref={emblaRef} sx={{ overflow: 'hidden', borderRadius: 2 }}>
            <Grid container flexWrap="nowrap">
              {displayImages.map((image, index) => (
                <Box
                  key={index}
                  onClick={() => openGallery(index)}
                  sx={{
                    flex: '0 0 100%',
                    height: MOBILE_HEIGHT,
                    cursor: 'pointer',
                  }}>
                  {renderMedia(image, `${experienceName} ${index + 1}`, MOBILE_IMAGE_WIDTH)}
                </Box>
              ))}
            </Grid>
          </Box>
          {displayImages.length > 1 && (
            <>
              <IconButton
                onClick={scrollPrev}
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 1,
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  boxShadow: 1,
                  '&:hover': { bgcolor: 'white' },
                }}>
                {isRtl ? <ChevronRight /> : <ChevronLeft />}
              </IconButton>
              <IconButton
                onClick={scrollNext}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 1,
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  boxShadow: 1,
                  '&:hover': { bgcolor: 'white' },
                }}>
                {isRtl ? <ChevronLeft /> : <ChevronRight />}
              </IconButton>
            </>
          )}
        </Box>
        {renderGalleryModal()}
      </>
    );
  }

  // Desktop: CSS Grid layout (show max 3 images)
  const mainImage = displayImages[0];
  const secondaryImages = displayImages.slice(1, MAX_VISIBLE_IMAGES);
  const hasMoreImages = displayImages.length > MAX_VISIBLE_IMAGES;
  const extraImagesCount = displayImages.length - MAX_VISIBLE_IMAGES;

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: secondaryImages.length > 0 ? '2fr 1fr' : '1fr',
          gridTemplateRows: secondaryImages.length > 1 ? `repeat(${secondaryImages.length}, 1fr)` : '1fr',
          gap: 1,
          height: DESKTOP_HEIGHT,
        }}>
        {/* Main Image */}
        <Box
          onClick={() => openGallery(0)}
          sx={{
            gridRow: secondaryImages.length > 1 ? `span ${secondaryImages.length}` : 'span 1',
            overflow: 'hidden',
            borderRadius: `${IMAGE_BORDER_RADIUS}px`,
            cursor: 'pointer',
          }}>
          {renderMedia(mainImage, experienceName, DESKTOP_MAIN_IMAGE_WIDTH)}
        </Box>

        {/* Secondary Images */}
        {secondaryImages.map((image, index) => {
          const isLastImage = index === secondaryImages.length - 1;
          const showChip = isLastImage && hasMoreImages;

          return (
            <Box
              key={index}
              onClick={() => (showChip ? openGallery(0) : openGallery(index + 1))}
              sx={{
                overflow: 'hidden',
                borderRadius: `${IMAGE_BORDER_RADIUS}px`,
                cursor: 'pointer',
                position: 'relative',
              }}>
              {renderMedia(image, `${experienceName} ${index + 2}`, DESKTOP_SECONDARY_IMAGE_WIDTH)}
              {showChip && (
                <Chip
                  icon={<Collections sx={{ fontSize: 18 }} />}
                  label={t('experiencePage.showAll', { count: extraImagesCount })}
                  onClick={(e) => {
                    e.stopPropagation();
                    openGallery(0);
                  }}
                  sx={{
                    position: 'absolute',
                    bottom: 12,
                    right: 12,
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'white' },
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
      {renderGalleryModal()}
    </>
  );
};
